import { Link } from "@tanstack/react-router";
import { FiImage, FiMic, FiPlus, FiTrash2, FiUpload } from "react-icons/fi";
import { MediaType } from "../../../generated/graphql";
import { SearchBar } from "./SearchBar";
import { ViewToggle } from "./ViewToggle";

interface MediaHeaderActionsProps {
  mediaType: MediaType;
  onNewFolder: () => void;
  onUpload: () => void;
  onGenerateAudio?: () => void;
  onGenerateImage?: () => void;
  searchValue: string;
  onSearchChange: (query: string) => void;
  onSearchClear: () => void;
  trashCount?: number;
}

// Helper function to get trash route based on media type
function getTrashRoute(mediaType: MediaType): string {
  const typeMap: Record<MediaType, string> = {
    [MediaType.Audio]: "/media/audio/trash",
    [MediaType.Image]: "/media/images/trash",
    [MediaType.Video]: "/media/videos/trash",
    [MediaType.File]: "/media/files/trash",
  };
  return typeMap[mediaType];
}

export function MediaHeaderActions({
  mediaType,
  onNewFolder,
  onUpload,
  onGenerateAudio,
  onGenerateImage,
  searchValue,
  onSearchChange,
  onSearchClear,
  trashCount = 0,
}: MediaHeaderActionsProps) {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
      {/* Left: Search */}
      <SearchBar
        mediaType={mediaType}
        value={searchValue}
        onChange={onSearchChange}
        onClear={onSearchClear}
      />

      {/* Right: Actions */}
      <div className="flex items-center gap-2">
        {/* Trash Button */}
        {trashCount > 0 && (
          <Link
            to={getTrashRoute(mediaType) as string}
            className="flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <FiTrash2 className="h-4 w-4" />
            <span>Trash ({trashCount})</span>
          </Link>
        )}

        {/* New Folder Button */}
        <button
          type="button"
          onClick={onNewFolder}
          className="flex items-center gap-1.5 rounded-md bg-white border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          <FiPlus className="h-4 w-4" />
          <span>New folder</span>
        </button>

        {/* Generate Audio Button (Audio media type only) */}
        {mediaType === MediaType.Audio && onGenerateAudio && (
          <button
            type="button"
            onClick={onGenerateAudio}
            className="flex items-center gap-1.5 rounded-md bg-purple-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors"
          >
            <FiMic className="h-4 w-4" />
            <span>New Audio</span>
          </button>
        )}

        {/* Generate Image Button (Image media type only) */}
        {mediaType === MediaType.Image && onGenerateImage && (
          <button
            type="button"
            onClick={onGenerateImage}
            className="flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
          >
            <FiImage className="h-4 w-4" />
            <span>New Image</span>
          </button>
        )}

        {/* Upload Button */}
        <button
          type="button"
          onClick={onUpload}
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          <FiUpload className="h-4 w-4" />
          <span>Upload</span>
        </button>

        {/* View Toggle */}
        <ViewToggle />
      </div>
    </div>
  );
}
